import { BaseText, ListItem, Meta, theme } from 'aimo-ui';
import { TitleWrapper } from 'components/account/paymentMethods/PaymentMethodItem';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useShowPaymentCardStatus from 'hooks/useShowPaymentCardStatus';
import { default as React, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { CenterRow } from 'styles/ContainerStyles';
import { Card, CardStatus, Maybe } from 'types/generatedSchemaTypes';

import { usePaymentStore } from 'stores/paymentStore';
import styled from 'styled-components/native';
import { Tag, TagText } from 'styles/TextStyles';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import {
  CardAccessibilityData,
  getCardAccessibilityLabel,
  getCardLogo,
  getCardMaskedLastNums,
  getWalletPaymentMethodIcon,
  getWalletPaymentMethodLabelKey,
  isWalletCard,
} from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

type CardItemProps = {
  item: Maybe<Card>;
  indexValue: number;
};

export const TitleCheckWrapper = styled.View`
  margin-right: 4px;
  margin-left: 5px;
  justify-content: center;
`;

const IconWrapper = styled.View`
  margin-right: 4px;
  margin-top: 4px;
`;

const ArrowWrapper = styled.View`
  margin-right: -4px;
  margin-top: 4px;
`;

const ArrowContainer = ({
  item,
  testID,
}: {
  item: Maybe<Card>;
  testID: string;
}) => {
  const { isErrorStatus, expiresSoon } = useShowPaymentCardStatus(
    (item?.status ?? 'Active') as CardStatus,
    item?.expMonth ?? '',
    item?.expYear ?? ''
  );
  const isBusiness = item?.category === 'business';
  const isVerifying = item?.status === 'Verifying';
  const { t } = useTranslation();
  const {
    flags: { card_business },
  } = useContext(FeatureFlagsContext);
  const showBusinessCard = showFeatureFlag(card_business);

  return (
    <CenterRow>
      {(isErrorStatus || expiresSoon) && (
        <IconWrapper>
          <Icon
            name={`Error-Filled${expiresSoon ? '-Black' : ''}`}
            size="m"
            {...testIdentifiers(
              `${testID}-${expiresSoon ? 'warning' : 'error'}-icon`
            )}
            color={
              expiresSoon ? theme.colors.boost.yellow : theme.colors.aimoUiRed
            }
          />
        </IconWrapper>
      )}
      {isBusiness && showBusinessCard && (
        <Tag {...testIdentifiers(`${testID}-business-tag`)}>
          <TagText>{t('paymentMethods.business')}</TagText>
        </Tag>
      )}
      {isVerifying && (
        <Tag {...testIdentifiers(`${testID}-verifying-tag`)}>
          <TagText>{t('paymentMethods.verifying')}</TagText>
        </Tag>
      )}
      <ArrowWrapper>
        <Icon
          name="Chevron_Right"
          {...testIdentifiers(`${testID}-chevron-right`)}
          size="s"
        />
      </ArrowWrapper>
    </CenterRow>
  );
};

const IconComponent = ({ item, indexValue }: CardItemProps) => (
  <Icon
    {...testIdentifiers(`credit-card-icon-${indexValue}`)}
    name={
      isWalletCard(item)
        ? getWalletPaymentMethodIcon(item?.paymentMethod)
        : `${getCardLogo(item?.type)}`
    }
    size="l"
  />
);

// Title for a card row: the customer's custom name (description) always wins;
// a wallet card without a name falls back to its wallet label (Apple Pay/
// Google Pay), a plain card to its scheme type.
const getCardTitle = (
  item: Maybe<Card>,
  walletLabelKey: string | undefined,
  t: (key: string) => string
): Maybe<string> | undefined => {
  if (item?.description) {
    return item.description;
  }
  if (walletLabelKey) {
    return t(walletLabelKey);
  }
  return item?.type;
};

const TitleComponent = ({
  item,
  indexValue,
  isDefaultPaymentOption,
  t,
}: CardItemProps & {
  isDefaultPaymentOption: boolean;
  t: (key: string) => string;
}) => {
  const walletLabelKey = getWalletPaymentMethodLabelKey(item?.paymentMethod);
  const title = getCardTitle(item, walletLabelKey, t);
  return (
    <TitleWrapper>
      <BaseText
        numberOfLines={1}
        {...testIdentifiers(`credit-card-title-${indexValue}`)}>
        {title}
      </BaseText>
      {isDefaultPaymentOption && (
        <TitleCheckWrapper>
          <Icon
            name="check"
            size="xs"
            {...testIdentifiers(`credit-card-is-default-${indexValue}`)}
            color={theme.colors.black}
          />
        </TitleCheckWrapper>
      )}
    </TitleWrapper>
  );
};

// A stored wallet card (Apple Pay/Google Pay) has no meaningful PAN to show —
// suppress the "**** 1234" subtitle entirely for it.
const SubtitleComponent = ({ item, indexValue }: CardItemProps) => {
  if (isWalletCard(item)) {
    return null;
  }
  return (
    <Meta
      {...testIdentifiers(`credit-card-number-${indexValue}`)}
      color={theme.colors.gray300}>
      {`**** ${item?.lastNums ? item.lastNums : ''}`}
    </Meta>
  );
};

const RightContentComponent = ({ item, indexValue }: CardItemProps) => (
  <ArrowContainer item={item} testID={`credit-card-${indexValue}`} />
);

const CardItem = ({ item, indexValue }: CardItemProps) => {
  const { t } = useTranslation();
  const {
    customer: { paymentOptions },
  } = useContext(AccountContext);

  // Fall back to card.isDefault for accounts without paymentOptions yet
  const isDefaultPaymentOption = paymentOptions?.length
    ? (paymentOptions.some(
        (o) =>
          o?.isDefaultTokenizedPaymentOption && o?.optionId === item?.cardId
      ) ?? false)
    : item?.isDefault === true;

  const selectCard = () => {
    // Same wallet-aware projection as the row itself (and handleActivated's
    // post-add path): a stored wallet card has no meaningful description/type
    // or PAN, so the paymentCard modal gets the wallet label and no subtitle.
    const walletLabelKey = getWalletPaymentMethodLabelKey(item?.paymentMethod);
    usePaymentStore.getState().setSelectedPaymentCard({
      cardId: item?.cardId || '',
      title: getCardTitle(item, walletLabelKey, t) || '',
      subtitle: walletLabelKey ? '' : getCardMaskedLastNums(item),
      category: `${item?.category || ''}`,
      status: `${item?.status || ''}`,
      isDefault: isDefaultPaymentOption,
      description: item?.description || '',
      newCard: false,
      expMonth: item?.expMonth || '',
      expYear: item?.expYear || '',
    });
    useUiStore.getState().setSheetModal('paymentCard');
  };

  // Get card properties for accessibility label
  const cardDescription = item?.description ?? item?.type ?? '';
  const lastDigits = item?.lastNums ?? '';
  const { isErrorStatus, expiresSoon } = useShowPaymentCardStatus(
    (item?.status ?? 'Active') as CardStatus,
    item?.expMonth ?? '',
    item?.expYear ?? ''
  );
  const isBusiness = item?.category === 'business';
  const isVerifying = item?.status === 'Verifying';

  // Create card data object for accessibility label
  const cardData: CardAccessibilityData = {
    cardDescription,
    lastDigits,
    isDefault: isDefaultPaymentOption,
    isErrorStatus,
    expiresSoon,
    isVerifying,
    isBusiness,
  };

  // Use the utility function to generate the accessibility label
  const accessibilityLabel = getCardAccessibilityLabel(cardData, t);

  return (
    <ListItem
      item={item}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessible={true}
      indexValue={indexValue}
      onPress={selectCard}
      iconComponent={() => IconComponent({ item, indexValue })}
      titleComponent={() =>
        TitleComponent({ item, indexValue, isDefaultPaymentOption, t })
      }
      subtitleComponent={() => SubtitleComponent({ item, indexValue })}
      rightContentComponent={() => RightContentComponent({ item, indexValue })}
    />
  );
};

export default CardItem;
